/**
 * Wasser (18.10.2026): wie viel man getrunken hat, gegen ein Tagesziel.
 *
 * Vorgabe 1500 ml: Die DGE empfiehlt Erwachsenen rund 1,5 Liter Getränke am
 * Tag (Quelle in docs/fachkonzept.md). Mehr bei Sport oder Hitze — eine Zahl
 * dafür steht nicht in der Quelle, die App erfindet deshalb keine. Das Ziel
 * lässt sich am Profil ändern.
 *
 * Getrunken wird als Zeilen (`water_log`), nicht als Zähler: Zwei Geräte
 * zählen sonst gegeneinander.
 */

import { stableId } from './ids.js';

export const WATER_DEFAULT_TARGET_ML = 1500;
export const WATER_GLASS_ML = 250;
export const WATER_BOTTLE_ML = 500;
export const WATER_MAX_ENTRY_ML = 3000;

/** Die Mittagsfrage „Wie viel hast du getrunken?“. */
export const WATER_MIDDAY = '13:00';
/** Wahlweise: nachfragen, wenn man um diese Uhrzeiten zurückliegt. */
export const WATER_PACE_CHECKS = ['11:00', '15:00', '18:00'] as const;
/** Über diesen Zeitraum verteilt sich das Ziel gleichmäßig. */
export const WATER_DAY_START = '08:00';
export const WATER_DAY_END = '20:00';
/** Erst ab so viel Rückstand fragt die App nach — ein Glas. */
export const WATER_BEHIND_ML = WATER_GLASS_ML;

export interface WaterSettings {
  targetMl: number;
  /** Mittagsfrage (ab Werk an, abschaltbar). */
  midday: boolean;
  /** Nachfragen bei Rückstand (ab Werk aus). */
  pace: boolean;
}

/** Gespeichertes lesen — `null` oder Unsinn heißt Vorgabe. */
export function waterSettings(stored: unknown): WaterSettings {
  const raw = (stored && typeof stored === 'object' ? stored : {}) as Record<string, unknown>;
  const target =
    typeof raw.targetMl === 'number' && raw.targetMl >= 250 && raw.targetMl <= 10_000
      ? Math.round(raw.targetMl)
      : WATER_DEFAULT_TARGET_ML;
  return {
    targetMl: target,
    midday: raw.midday !== false,
    pace: raw.pace === true,
  };
}

function minutes(time: string): number {
  const [hours, mins] = time.split(':').map(Number);
  return (hours ?? 0) * 60 + (mins ?? 0);
}

/** Wie viel bis zu dieser Uhrzeit dran wäre — gleichmäßig von 8 bis 20 Uhr. */
export function waterDueBy(targetMl: number, time: string): number {
  const start = minutes(WATER_DAY_START);
  const end = minutes(WATER_DAY_END);
  const share = Math.min(1, Math.max(0, (minutes(time) - start) / (end - start)));
  return Math.round((targetMl * share) / 50) * 50;
}

/** Rückstand in ml (0, wenn man im Plan oder darüber ist). */
export function waterBehind(drunkMl: number, targetMl: number, time: string): number {
  return Math.max(0, waterDueBy(targetMl, time) - drunkMl);
}

/** Eine Erinnerung je Konto, Tag und Uhrzeit. */
export function waterReminderRef(userId: string, date: string, time: string): string {
  return stableId(`water-reminder|${userId}|${date}|${time}`);
}
